import React, { memo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { defaultMemoize } from 'reselect';
import LaunchIcon from '@material-ui/icons/Launch';
import Loader from '@ublend-npm/aulaui-next-web/Loader';

import {
  TopBar,
  Provider,
  Container,
  DropdownStyle,
  ProviderTitle,
  AssignmentTitle,
  DropdownWrapper,
  DropdownItemStyle,
  NavigationInlineStyle,
  SubmissionListHeaderText,
  SubmissionCount,
  SubmissionListItem as SubmissionListHeader,
} from './Submissions.styled';
import { Select } from '../../common/FieldSets';
import SubmissionsList from './SubmissionsList';
import SubmissionGroups from './FilterSubmissions/FilterSubmissions';
import { LogoComponent } from '../Assignments/List/List';
import { MANAGE_GROUPS, FOOTER_ACTIONS } from './constants';
import launchHandin from '../Assignments/utils/launchHandin';
import groupSubmissionsByDate from './groupSubmissionsByDate';
import TurnitinLogo from '../../../../../static/turnitin.png';
import HandinLogo from '../../../../../static/handin-logo.svg';
import PostNavigation from '../Feed/PostNavigation/PostNavigation.container';
import { HANDIN_PROVIDER, TURNITIN_PROVIDER } from '../Assignments/constants';
import formatGroupSubmissions from './GroupSubmissions/formatGroupSubmissions';
import { analyticsTrackEvent } from '../../../../../core/utils/analytics';
import * as analyticEvents from '../../../../../core/constants/analytics';

const getFormattedGroupSubmissions = defaultMemoize((submissions) =>
  formatGroupSubmissions(submissions)
);

const getStudentsWithNoGroup = defaultMemoize((groupSet, sets) =>
  sets.find((set) => set.key === groupSet)
);

const SubmissionsPage = memo((props) => {
  const {
    user,
    sets,
    spaceName,
    accessToken,
    assignmentId,
    submissions,
    isFetchingSubmissions,
    fetchSubmissionsErrored,
    hasFetchedSubmissions,
    launchTurnitinAssignment,
    launchTurnitinSubmission,
    fetchSubmissions,
    assignment,
    hasFetchedAssignments,
    fetchAssignmentsErrored,
    isFetchingAssignments,
    fetchAssignments,
    accessingAsStaff,
  } = props;
  const [selectedGroup, setSelectedGroup] = useState(null);

  const handleFetchAssignments = async () => {
    if (!hasFetchedAssignments && !isFetchingAssignments) {
      const {
        selectedSpace: { objectId: spaceId },
      } = props;
      fetchAssignments({
        user,
        accessToken,
        spaceId,
      });
    }
  };

  const handleFetchSubmissions = async (group = null) => {
    if (!isFetchingSubmissions) {
      fetchSubmissions({ accessToken, assignmentId, group });
    }
    if (group) {
      const {
        selectedSpace: { objectId: spaceId },
      } = props;
      analyticsTrackEvent(analyticEvents.FILTER_SUBMISSIONS_BY_GROUP_ID, {
        activeClassroom: spaceId,
        assignmentId,
        groupId: group,
      });
    } else {
      setSelectedGroup(null);
    }
  };

  const updateDocumentTitle = () => {
    const space = spaceName ? `${spaceName} - ` : '';
    document.title = `Submissions - ${space}Aula`;
  };

  const prevAccessToken = useRef();
  useEffect(() => {
    if (accessToken && !prevAccessToken.current) {
      handleFetchAssignments();
      handleFetchSubmissions();
      prevAccessToken.current = accessToken;
    }
  }, [accessToken]);

  useEffect(() => {
    updateDocumentTitle();
  }, [spaceName]);

  const launchAssignment = ({ userId = null } = {}) => {
    const {
      selectedSpace: { objectId: spaceId },
    } = props;
    switch (assignment.provider) {
      case HANDIN_PROVIDER:
        launchHandin({
          assignmentId: assignment.externalId,
          spaceId: assignment.classRoom
            ? assignment.classRoom.objectId
            : spaceId,
          studentId: userId,
        });
        break;
      case TURNITIN_PROVIDER:
        launchTurnitinAssignment({
          user,
          assignment,
          spaceId,
        });
        break;
      default:
        break;
    }
  };

  const launchTIISubmission = ({ submissionId }) => {
    const {
      selectedSpace: { objectId: spaceId },
    } = props;
    launchTurnitinSubmission({
      submissionId,
      spaceId,
    });
  };

  const handleSelectChange = (key) => {
    switch (key) {
      case FOOTER_ACTIONS:
        setSelectedGroup(null);
        handleFetchSubmissions();
        break;
      case MANAGE_GROUPS: {
        const {
          selectedSpace: { objectId: spaceId },
          openGroups,
        } = props;
        openGroups(spaceId);
        break;
      }
      default:
        setSelectedGroup(key);
        handleFetchSubmissions(key);
        break;
    }
  };

  const ProviderConfig = {
    'hand-in': {
      title: 'Handin',
      image: HandinLogo,
      handler: launchAssignment,
    },
    turnitin: {
      title: 'Turnitin',
      image: TurnitinLogo,
      handler: launchTIISubmission,
    },
  };

  const hasError = fetchSubmissionsErrored || fetchAssignmentsErrored;

  if (hasError) {
    return (
      <Container>
        <TopBar>
          <PostNavigation
            style={NavigationInlineStyle}
            prevPage="all assignments"
          />
        </TopBar>
        <TopBar className="loader">
          <p>An error occured while loading submissions. Please refresh!</p>
        </TopBar>
      </Container>
    );
  }

  const isGroupAssignment = !!assignment.groupSet;
  const submissionsData = isGroupAssignment
    ? getFormattedGroupSubmissions(submissions)
    : submissions;
  const submissionGroups = groupSubmissionsByDate(
    submissionsData,
    assignment.endDate
  );
  const { earlySubmissions, lateSubmissions, noSubmissions } = submissionGroups;
  // @totalCount gets the correct count for students/groups in individul & group assignments
  // submission length would give incorrect count for group assignments
  const totalCount =
    earlySubmissions.length + lateSubmissions.length + noSubmissions.length;
  const totalSubmissions = earlySubmissions.length + lateSubmissions.length;
  const Logo = assignment.provider
    ? LogoComponent(ProviderConfig[assignment.provider].image)
    : '';
  const loading = !hasFetchedAssignments || !hasFetchedSubmissions;
  const { studentsWithNoGroup } = getStudentsWithNoGroup(
    assignment.groupSet,
    sets
  ) || { studentsWithNoGroup: [] };

  return (
    <Container>
      <TopBar>
        <PostNavigation
          style={NavigationInlineStyle}
          prevPage="all assignments"
        />
        {!loading &&
        (!accessingAsStaff || assignment.provider !== TURNITIN_PROVIDER) ? (
          <Provider onClick={launchAssignment}>
            <Logo />
            <ProviderTitle>
              Open in {ProviderConfig[assignment.provider].title}
              <LaunchIcon fontSize="small" />
            </ProviderTitle>
          </Provider>
        ) : null}
      </TopBar>

      {loading ? (
        <TopBar className="loader">
          <Loader size={25} />
        </TopBar>
      ) : (
        <>
          <AssignmentTitle data-testid="assignment-title">
            {assignment.title}
          </AssignmentTitle>
          <DropdownWrapper id="group-dropdown">
            <Select
              label="Group"
              id="tags-select"
              selectedKey={selectedGroup}
              onChange={handleSelectChange}
              onDismiss={selectedGroup && handleSelectChange}
              dataSet={sets}
              DropdownStyle={DropdownStyle}
              DropdownItemType={SubmissionGroups}
              headerTextTruncate
              isLoading={false}
              headerFontSize="medium"
              showKeyInHeader
              ariaLabelledby="group-dropdown"
              DropdownItemStyle={DropdownItemStyle}
            />
          </DropdownWrapper>
          <SubmissionCount>
            Submissions{' '}
            <span>
              ({totalSubmissions}/{totalCount})
            </span>
          </SubmissionCount>

          <SubmissionListHeader isHeader>
            <SubmissionListHeaderText primary="Student" />
            <SubmissionListHeaderText primary="Submission Date" dateHeader />
            <SubmissionListHeaderText primary="Grade" />
          </SubmissionListHeader>

          <SubmissionsList
            provider={assignment.provider}
            submissions={submissions}
            ProviderConfig={ProviderConfig}
            launchAssignment={launchAssignment}
            submissionGroups={submissionGroups}
            loading={loading}
            assignmentId={assignmentId}
            isGroupAssignment={isGroupAssignment}
            studentsWithNoGroup={studentsWithNoGroup}
            accessingAsStaff={accessingAsStaff}
          />
        </>
      )}
    </Container>
  );
});

SubmissionsPage.propTypes = {
  sets: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    firstName: PropTypes.string,
    sessionToken: PropTypes.string,
  }).isRequired,
  selectedSpace: PropTypes.shape({
    objectId: PropTypes.string,
  }),
  assignment: PropTypes.shape({
    title: PropTypes.string,
    groupSet: PropTypes.string,
    provider: PropTypes.string,
    endDate: PropTypes.string,
    externalId: PropTypes.string,
    classRoom: PropTypes.shape({
      objectId: PropTypes.string,
    }),
  }),
  submissions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isFetchingSubmissions: PropTypes.bool.isRequired,
  fetchSubmissionsErrored: PropTypes.bool.isRequired,
  hasFetchedSubmissions: PropTypes.bool.isRequired,
  accessToken: PropTypes.string,
  spaceName: PropTypes.string,
  assignmentId: PropTypes.string.isRequired,
  openGroups: PropTypes.func.isRequired,
  launchTurnitinAssignment: PropTypes.func.isRequired,
  launchTurnitinSubmission: PropTypes.func.isRequired,
  fetchSubmissions: PropTypes.func.isRequired,
  hasFetchedAssignments: PropTypes.bool.isRequired,
  fetchAssignmentsErrored: PropTypes.bool.isRequired,
  isFetchingAssignments: PropTypes.bool.isRequired,
  fetchAssignments: PropTypes.func.isRequired,
  accessingAsStaff: PropTypes.bool.isRequired,
};

SubmissionsPage.defaultProps = {
  accessToken: undefined,
  assignment: {},
  selectedSpace: undefined,
  spaceName: '',
};

export default SubmissionsPage;
